import { HelpBtn } from './HelpBtn';
import { useRef, useState } from 'react';
import ImportReview from './ImportReview';
import { toast } from 'sonner';
import { Globe, Route, Sheet, Upload, DraftingCompass, FileDown } from 'lucide-react';
import CadPanel from './CadPanel';
import { setVis, flyTo, pipeCat, fitCat } from '../lib/vis';
import { pipeLatLngs } from '../lib/model';
import { useFeatures } from '../lib/features';
import { importGeoJSON, importGPX, importTable, validateTable, templateFiles } from '../lib/importers';

const dl = (b, n) => { const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = n; a.click(); };

// Import center (mirrors Export & Share): DXF/DWG, GeoJSON, GPX, CSV/XLSX, project backup
export default function ImportCenter({ t, project, updateProject, onBackup, getCenter }) {
  const [rev, setRev] = useState(null);
  const ft = useFeatures();
  const refs = { geo: useRef(null), gpx: useRef(null), tab: useRef(null), bak: useRef(null) };
  const near = project.lat != null ? [project.lat, project.lon] : project.reports?.[0] ? [project.reports[0].lat, project.reports[0].lon] : null;
  const merge = ({ pipes = [], fittings = [], tracks = [] }) => {
    updateProject((p) => ({ ...p, pipes: [...p.pipes, ...pipes], fittings: [...(p.fittings || []), ...fittings], tracks: [...(p.tracks || []), ...tracks.map((tr, i) => ({ ...tr, no: (p.tracks || []).length + i + 1 }))] }));
    const all = [...(project.fittings || []), ...fittings];
    setVis(Object.fromEntries([...pipes.map(pipeCat), ...fittings.map(fitCat), ...(tracks.length ? ['tracks'] : [])].map((k) => [k, true])));
    flyTo([...pipes.flatMap((p) => pipeLatLngs(p, all)), ...fittings.map((f) => [f.lat, f.lon]), ...tracks.flatMap((tr) => tr.pts.map((p) => [p.lat, p.lon]))].filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1])));
    toast.success(`${t.impDone}: ${pipes.length} ${t.impPipes} · ${fittings.length} ${t.impObjs} · ${tracks.length} ${t.impTracks}`);
  };
  const run = (fn) => async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try { await fn(f); } catch { toast.error(t.impErr); }
  };
  const geo = run(async (f) => { const r = importGeoJSON(await f.text(), near); if (!r.pipes.length && !r.fittings.length) throw new Error('empty'); merge(r); });
  const gpx = run(async (f) => {
    const r = importGPX(await f.text());
    const fits = r.wps.map((w) => ({ id: w.id, type: 'other', name: w.note.slice(0, 80) || 'WPT', state: 'none', note: w.note, lat: w.lat, lon: w.lon, media: [], memos: [], imported: 'gpx', ...(w.alt != null ? { elev: { v: w.alt, src: 'gps', at: Date.now() } } : {}) }));
    if (!r.tracks.length && !fits.length) throw new Error('empty');
    merge({ tracks: r.tracks, fittings: fits });
  });
  const tab = run(async (f) => {
    const buf = new Uint8Array(await f.arrayBuffer());
    const v = validateTable(buf, project);
    if (!v.legacy) return setRev(v);
    const r = importTable(buf, near);
    if (!r.fittings.length) throw new Error('empty');
    merge(r);
  });
  const Btn = ({ k, icon: Icon, label, ext, accept, on, cls = 'btn-outline' }) => (
    <>
      <button className={`btn ${cls} imp-btn`} data-testid={`import-${k}-btn`} onClick={() => refs[k].current.click()}><Icon size={16} /> <span>{label}</span><small>{ext}</small></button>
      <input ref={refs[k]} type="file" accept={accept} hidden data-testid={`import-${k}-input`} onChange={on} />
    </>
  );
  return (
    <section className="io-col imp-center" data-testid="import-center">
      <h3 className="sec"><span>06</span>{t.impTitle}<HelpBtn k="n:cadimport" testid="sec06-help" title={t.helpHere} /></h3>
      <p className="muted small-hint">{t.impHint}</p>
      {ft.cadio && <div className="imp-cad"><DraftingCompass size={15} /> <b>AutoCAD</b> <small>.dxf / .dwg</small></div>}
      {ft.cadio && <CadPanel t={t} project={project} updateProject={updateProject} getCenter={getCenter} />}
      <div className="imp-grid">
        {ft.cadio && <Btn k="geo" icon={Globe} label={t.impGeo} ext=".geojson / .json" accept=".geojson,.json,application/geo+json" on={geo} />}
        <Btn k="gpx" icon={Route} label={t.impGpx} ext=".gpx" accept=".gpx,application/gpx+xml" on={gpx} />
        {(ft.fittings || ft.cadio) && <Btn k="tab" icon={Sheet} label={t.impTab} ext=".xlsx / .csv" accept=".xlsx,.xls,.csv,text/csv" on={tab} />}
        <Btn k="bak" icon={Upload} label={t.btnLoadJson} ext=".giss" accept=".giss,.json,application/json" on={onBackup} cls="btn-proj" />
      </div>
      {(ft.fittings || ft.cadio) && <div className="row imp-tpl">
        <small>{t.impTplHint}</small>
        <button className="btn btn-outline btn-sm" data-testid="import-template-xlsx-btn" onClick={() => dl(templateFiles().xlsx, 'GISS_Import_Vorlage.xlsx')}><FileDown size={14} /> {t.impTpl} .xlsx</button>
        <button className="btn btn-outline btn-sm" data-testid="import-template-csv-btn" onClick={() => dl(templateFiles().csv, 'GISS_Import_Vorlage.csv')}><FileDown size={14} /> {t.impTpl} .csv</button>
      </div>}
      {rev && <ImportReview t={t} res={rev} onClose={() => setRev(null)} onImport={(fits) => { merge({ fittings: fits }); setRev(null); }} />}
    </section>
  );
}
